/**
 * PredictionRequestForm — 4-step guided prediction launch.
 * Phase 4.10 β3 — WQ-238.
 *
 * Modes:
 *   single — synchronous /predict; returns PredictResponse inline at Step 4.
 *   batch  — async /predict/batch; returns batch_id and auto-closes,
 *            then PredictionPanel polling refreshes the list.
 *
 * Steps:
 *   1.  Mode toggle (single vs batch)
 *   2.  Model picker — list of deployed champion models
 *   3a. (single) Input values — schema-driven form OR free-text JSON fallback
 *   3b. (batch)  Catalog dataset picker + optional row limit
 *   4.  Review & Submit → result handling diverges by mode
 *
 * Local state machine; no React Router dependency. Closes on submit
 * success (batch) or via Close button after viewing the result (single)
 * or Cancel from any step.
 *
 * Reuses the WQ-237 AutoMLWizard structural pattern (state machine
 * + step components + validation + review-before-submit + dual-source
 * picker for batch). Does NOT import from AutoMLWizard — copy-paste
 * with divergence per WQ-237 NOTES.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  predict,
  predictBatch,
  listDeployedModels,
  getModelSchema,
  type PredictResponse,
  type ModelOption,
  type ModelSchemaResponse,
} from '../../services/predictionBIService';
import { listAvailableDatasets } from '../../services/datasetService';
import WizardModalShell from '../wizard/WizardModalShell';

type Mode = 'single' | 'batch';
type Step = 1 | 2 | 3 | 4;

interface CatalogEntryOption {
  id: string;
  name: string;
  row_count: number | null;
}

interface PredictionRequestFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted: (result: { mode: Mode; predictionId?: string; batchId?: string }) => void;
}

const STEP_TITLES: Record<Step, { single: string; batch: string }> = {
  1: { single: 'Pick a mode', batch: 'Pick a mode' },
  2: { single: 'Choose a model', batch: 'Choose a model' },
  3: { single: 'Provide inputs', batch: 'Choose a catalog dataset' },
  4: { single: 'Review & submit', batch: 'Review & submit' },
};

const PredictionRequestForm: React.FC<PredictionRequestFormProps> = ({
  isOpen,
  onClose,
  onSubmitted,
}) => {
  const { authFetch } = useAuth();
  const [step, setStep] = useState<Step>(1);
  const [mode, setMode] = useState<Mode>('single');

  // Step 2 state
  const [models, setModels] = useState<ModelOption[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<ModelOption | null>(null);

  // Step 3 (single) state
  const [schema, setSchema] = useState<ModelSchemaResponse | null>(null);
  const [schemaChecked, setSchemaChecked] = useState(false);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [jsonText, setJsonText] = useState<string>('{}');
  const [useJsonFallback, setUseJsonFallback] = useState(false);

  // Step 3 (batch) state
  const [catalogEntries, setCatalogEntries] = useState<CatalogEntryOption[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [selectedCatalog, setSelectedCatalog] = useState<CatalogEntryOption | null>(null);
  const [rowLimit, setRowLimit] = useState<number | ''>('');

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [singleResult, setSingleResult] = useState<PredictResponse | null>(null);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setMode('single');
      setSelectedModel(null);
      setSchema(null);
      setSchemaChecked(false);
      setInputValues({});
      setJsonText('{}');
      setUseJsonFallback(false);
      setSelectedCatalog(null);
      setRowLimit('');
      setSubmitError(null);
      setSingleResult(null);
    }
  }, [isOpen]);

  // Load models when form opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setModelsLoading(true);
      setModelsError(null);
      try {
        const data = await listDeployedModels(authFetch);
        if (!cancelled) setModels(data);
      } catch (err) {
        if (!cancelled) setModelsError(err instanceof Error ? err.message : 'Failed to load models');
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, authFetch]);

  // Load catalog entries when batch + step >= 3
  useEffect(() => {
    if (!isOpen || mode !== 'batch' || step < 3) return;
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      setCatalogError(null);
      try {
        const data = await listAvailableDatasets(authFetch);
        if (cancelled) return;
        // Backend /predict/batch endpoint accepts catalog_entry_id only;
        // composed datasets are out of scope for WQ-238 per guidance.
        setCatalogEntries(
          (data.catalog ?? []).map((c) => ({
            id: c.id,
            name: c.name,
            row_count: c.row_count,
          }))
        );
      } catch (err) {
        if (!cancelled) setCatalogError(err instanceof Error ? err.message : 'Failed to load catalog');
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, mode, step, authFetch]);

  // Try schema fetch when single mode + model selected
  useEffect(() => {
    if (mode !== 'single' || !selectedModel) {
      setSchema(null);
      setSchemaChecked(false);
      setUseJsonFallback(false);
      return;
    }
    let cancelled = false;
    setSchemaChecked(false);
    (async () => {
      try {
        const result = await getModelSchema(authFetch, selectedModel.id);
        if (cancelled) return;
        if (result?.input_columns?.length) {
          setSchema(result);
          setUseJsonFallback(false);
        } else {
          setSchema(null);
          setUseJsonFallback(true);
        }
      } catch {
        if (!cancelled) {
          setSchema(null);
          setUseJsonFallback(true);
        }
      } finally {
        if (!cancelled) setSchemaChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, selectedModel, authFetch]);

  const jsonValid = useMemo(() => {
    if (!useJsonFallback) return true;
    try {
      const parsed = JSON.parse(jsonText);
      return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed);
    } catch {
      return false;
    }
  }, [useJsonFallback, jsonText]);

  const requiredInputsFilled = useMemo(() => {
    if (mode !== 'single' || useJsonFallback) return true;
    if (!schema?.input_columns?.length) return false;
    return schema.input_columns.every((c) => {
      if (c.required === false) return true;
      const v = inputValues[c.name];
      return v !== undefined && String(v).length > 0;
    });
  }, [mode, useJsonFallback, schema, inputValues]);

  const rowLimitValid = useMemo(() => {
    if (rowLimit === '') return true;
    const n = Number(rowLimit);
    return Number.isFinite(n) && n >= 1 && n <= 100000;
  }, [rowLimit]);

  const canAdvance = useMemo(() => {
    if (step === 1) return !!mode;
    if (step === 2) return !!selectedModel;
    if (step === 3 && mode === 'single') {
      return useJsonFallback ? jsonValid : requiredInputsFilled;
    }
    if (step === 3 && mode === 'batch') return !!selectedCatalog && rowLimitValid;
    return false;
  }, [step, mode, selectedModel, useJsonFallback, jsonValid, requiredInputsFilled, selectedCatalog, rowLimitValid]);

  const handleNext = () => {
    if (canAdvance && step < 4) setStep((step + 1) as Step);
  };
  const handleBack = () => {
    if (step > 1) setStep((step - 1) as Step);
  };

  const handleSubmit = async () => {
    if (!selectedModel) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (mode === 'single') {
        const inputData = useJsonFallback
          ? JSON.parse(jsonText)
          : Object.fromEntries(
              Object.entries(inputValues).map(([k, v]) => [k, coerceValue(v, schema, k)])
            );
        const result = await predict(authFetch, {
          model_id: selectedModel.id,
          input_data: inputData,
        });
        setSingleResult(result);
        onSubmitted({ mode: 'single', predictionId: result.id });
        // do NOT close; user reads inline result and clicks Close themselves
      } else {
        if (!selectedCatalog) return;
        const result = await predictBatch(authFetch, {
          model_id: selectedModel.id,
          catalog_entry_id: selectedCatalog.id,
          limit: rowLimit === '' ? undefined : Number(rowLimit),
        });
        onSubmitted({ mode: 'batch', batchId: result.batch_id });
        onClose();
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const stepTitle = STEP_TITLES[step][mode];

  return (
    <WizardModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="New Prediction"
      totalSteps={4}
      currentStep={step}
      stepTitle={stepTitle}
      testIdPrefix="prediction-form"
      canAdvance={canAdvance}
      submitting={submitting}
      onBack={handleBack}
      onNext={handleNext}
      onSubmit={handleSubmit}
      submitLabel="Submit"
      submittingLabel="Submitting..."
      submitTestId="prediction-form-submit"
      cancelLabel={singleResult ? 'Close' : 'Cancel'}
      hideBack={!!singleResult}
      hideSubmit={!!singleResult}
    >
      <>
          {step === 1 && (
            <div data-testid="prediction-form-step-1">
              <div style={{ display: 'flex', gap: 12 }}>
                {(['single', 'batch'] as Mode[]).map((m) => (
                  <button
                    key={m}
                    data-testid={`prediction-form-mode-${m}`}
                    onClick={() => setMode(m)}
                    style={{
                      flex: 1, padding: 16, borderRadius: 8, textAlign: 'left',
                      border: mode === m ? '1px solid var(--accent-primary)' : '1px solid var(--border)',
                      background: mode === m ? 'rgba(59,130,246,0.05)' : 'var(--bg-secondary)',
                      color: 'var(--text-primary)', cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 14, textTransform: 'capitalize' }}>{m} prediction</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                      {m === 'single'
                        ? 'Instant: provide input values; result returned immediately.'
                        : 'Async: pick a catalog dataset; predictions accumulate under a batch_id.'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="prediction-form-step-2">
              {modelsLoading && (
                <div data-testid="prediction-form-models-loading" style={{ color: 'var(--text-secondary)' }}>
                  Loading deployed models...
                </div>
              )}
              {modelsError && (
                <div data-testid="prediction-form-models-error" style={{
                  padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8,
                }}>
                  {modelsError}
                </div>
              )}
              {!modelsLoading && !modelsError && models.length === 0 && (
                <div data-testid="prediction-form-no-models" style={{
                  padding: 20, textAlign: 'center', color: 'var(--text-secondary)',
                  border: '1px dashed var(--border)', borderRadius: 8,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>No deployed models</div>
                  <div style={{ fontSize: 13 }}>
                    Run AutoML or register an algorithm first to deploy a model.
                  </div>
                </div>
              )}
              {models.length > 0 && (
                <div data-testid="prediction-form-models-list">
                  {models.map((m) => (
                    <button
                      key={m.id}
                      data-testid={`prediction-form-model-${m.id}`}
                      onClick={() => setSelectedModel(m)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '8px 12px', borderRadius: 6, marginBottom: 4,
                        border: selectedModel?.id === m.id ? '1px solid var(--accent-primary)' : '1px solid var(--border)',
                        background: selectedModel?.id === m.id ? 'rgba(59,130,246,0.05)' : 'var(--bg-secondary)',
                        color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13,
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{m.name}</span>
                      {m.problem_type && (
                        <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 8 }}>
                          ({m.problem_type})
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && mode === 'single' && (
            <div data-testid="prediction-form-step-3-single">
              {!schemaChecked && (
                <div data-testid="prediction-form-schema-loading" style={{ color: 'var(--text-secondary)' }}>
                  Resolving model input schema...
                </div>
              )}
              {schemaChecked && schema && !useJsonFallback && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      Fill the inputs the model expects.
                    </div>
                    <button
                      data-testid="prediction-form-toggle-json"
                      onClick={() => setUseJsonFallback(true)}
                      style={{
                        fontSize: 11, padding: '3px 8px', borderRadius: 4,
                        border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                        color: 'var(--text-primary)', cursor: 'pointer',
                      }}
                    >
                      Advanced: paste JSON
                    </button>
                  </div>
                  {schema.input_columns.map((c) => (
                    <div key={c.name} style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                        {c.name} ({c.dtype}){c.required === false ? '' : ' *'}
                      </label>
                      <input
                        data-testid={`prediction-form-input-${c.name}`}
                        type={isNumericDtype(c.dtype) ? 'number' : 'text'}
                        value={inputValues[c.name] ?? ''}
                        onChange={(e) => setInputValues({ ...inputValues, [c.name]: e.target.value })}
                        style={{
                          width: '100%', padding: '6px 10px', borderRadius: 6,
                          border: '1px solid var(--border)', background: 'var(--bg-primary)',
                          color: 'var(--text-primary)', fontSize: 13,
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
              {schemaChecked && useJsonFallback && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      Input as JSON (object with column → value pairs)
                    </label>
                    {schema && (
                      <button
                        data-testid="prediction-form-toggle-form"
                        onClick={() => setUseJsonFallback(false)}
                        style={{
                          fontSize: 11, padding: '3px 8px', borderRadius: 4,
                          border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                          color: 'var(--text-primary)', cursor: 'pointer',
                        }}
                      >
                        Use form
                      </button>
                    )}
                  </div>
                  <textarea
                    data-testid="prediction-form-json-input"
                    value={jsonText}
                    onChange={(e) => setJsonText(e.target.value)}
                    rows={8}
                    style={{
                      width: '100%', padding: 10, borderRadius: 6,
                      border: '1px solid var(--border)', background: 'var(--bg-primary)',
                      color: 'var(--text-primary)', fontSize: 12, fontFamily: 'monospace',
                      resize: 'vertical',
                    }}
                  />
                  {!jsonValid && (
                    <div data-testid="prediction-form-json-error" style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                      Invalid JSON. Provide an object like {`{"col": "value"}`}.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 3 && mode === 'batch' && (
            <div data-testid="prediction-form-step-3-batch">
              {catalogLoading && (
                <div data-testid="prediction-form-catalog-loading" style={{ color: 'var(--text-secondary)' }}>
                  Loading catalog entries...
                </div>
              )}
              {catalogError && (
                <div data-testid="prediction-form-catalog-error" style={{
                  padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8,
                }}>
                  {catalogError}
                </div>
              )}
              {!catalogLoading && !catalogError && catalogEntries.length === 0 && (
                <div data-testid="prediction-form-no-catalog" style={{
                  padding: 20, textAlign: 'center', color: 'var(--text-secondary)',
                }}>
                  No catalog entries available. Register one in Data Sources first.
                </div>
              )}
              {catalogEntries.length > 0 && (
                <>
                  <div style={{ marginBottom: 12 }} data-testid="prediction-form-catalog-list">
                    {catalogEntries.map((c) => (
                      <button
                        key={c.id}
                        data-testid={`prediction-form-catalog-${c.id}`}
                        onClick={() => setSelectedCatalog(c)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '8px 12px', borderRadius: 6, marginBottom: 4,
                          border: selectedCatalog?.id === c.id ? '1px solid var(--accent-primary)' : '1px solid var(--border)',
                          background: selectedCatalog?.id === c.id ? 'rgba(59,130,246,0.05)' : 'var(--bg-secondary)',
                          color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13,
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>{c.name}</span>
                      </button>
                    ))}
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
                      Row limit (optional, 1–100000)
                    </label>
                    <input
                      data-testid="prediction-form-row-limit"
                      type="number"
                      min={1}
                      max={100000}
                      value={rowLimit}
                      onChange={(e) => {
                        const v = e.target.value;
                        setRowLimit(v === '' ? '' : Number(v));
                      }}
                      placeholder="all rows"
                      style={{
                        width: '100%', padding: '6px 10px', borderRadius: 6,
                        border: '1px solid var(--border)', background: 'var(--bg-primary)',
                        color: 'var(--text-primary)', fontSize: 13,
                      }}
                    />
                    {!rowLimitValid && (
                      <div data-testid="prediction-form-row-limit-error" style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                        Row limit must be between 1 and 100000.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {step === 4 && selectedModel && (
            <div data-testid="prediction-form-step-4">
              {!singleResult && (
                <div data-testid="prediction-form-review" style={{
                  padding: 16, borderRadius: 8, border: '1px solid var(--border)',
                  background: 'var(--bg-secondary)', fontSize: 13, lineHeight: 1.6,
                }}>
                  Run <strong>{mode}</strong> prediction against <strong>{selectedModel.name}</strong>.
                  <br />
                  {mode === 'single' && (
                    <>
                      Input source: <strong>{useJsonFallback ? 'JSON' : 'form'}</strong>.
                    </>
                  )}
                  {mode === 'batch' && selectedCatalog && (
                    <>
                      Catalog dataset: <strong>{selectedCatalog.name}</strong>.<br />
                      Row limit: <strong>{rowLimit === '' ? 'all rows' : rowLimit}</strong>.
                    </>
                  )}
                </div>
              )}
              {submitError && (
                <div data-testid="prediction-form-submit-error" style={{
                  padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginTop: 12,
                }}>
                  {submitError}
                </div>
              )}
              {singleResult && (
                <div data-testid="prediction-form-result" style={{
                  padding: 16, borderRadius: 8, border: '1px solid rgba(34,197,94,0.3)',
                  background: 'rgba(34,197,94,0.05)', fontSize: 13, lineHeight: 1.6,
                }}>
                  <div style={{ fontWeight: 700, color: '#16a34a', marginBottom: 8 }}>
                    Prediction complete
                  </div>
                  <div>
                    Confidence:{' '}
                    <strong>
                      {singleResult.confidence_score !== null
                        ? `${(singleResult.confidence_score * 100).toFixed(1)}%`
                        : '—'}
                    </strong>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    Output:
                    <pre
                      data-testid="prediction-form-result-output"
                      style={{
                        marginTop: 4, padding: 8, borderRadius: 4,
                        background: 'var(--bg-primary)', border: '1px solid var(--border)',
                        fontSize: 11, fontFamily: 'monospace', overflow: 'auto', maxHeight: 200,
                      }}
                    >
                      {JSON.stringify(singleResult.output_value, null, 2)}
                    </pre>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 8 }}>
                    Prediction id: <code>{singleResult.id}</code>
                  </div>
                </div>
              )}
            </div>
          )}
      </>
    </WizardModalShell>
  );
};

function isNumericDtype(dtype: string): boolean {
  return /int|float|double|real|numeric/i.test(dtype);
}

function coerceValue(
  raw: string,
  schema: ModelSchemaResponse | null,
  columnName: string
): unknown {
  if (raw === '') return null;
  const col = schema?.input_columns.find((c) => c.name === columnName);
  if (!col) return raw;
  if (isNumericDtype(col.dtype)) {
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  }
  return raw;
}

export default PredictionRequestForm;
